/**
 * FirmwareReleases — admin-only firmware release management page.
 * List releases, promote status, view device version distribution.
 */
import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { format } from 'date-fns';
import { useGatewayFirmwareReleases } from '@/hooks/useGatewayFirmwareReleases';
import { useGatewayDevices } from '@/hooks/useGatewayDevices';
import type { GatewayFirmwareRelease, ReleaseStatus } from '@/types/gateway';
import { RELEASE_STATUS_CONFIG } from '@/types/gateway';
import { toast } from 'sonner';

const PROMOTION_MAP: Partial<Record<ReleaseStatus, { nextStatus: ReleaseStatus; label: string; description: string }>> = {
  draft: {
    nextStatus: 'testing',
    label: 'Promote to Testing',
    description: 'This will make the release visible to pilot devices.',
  },
  testing: {
    nextStatus: 'released',
    label: 'Promote to Released',
    description: 'This will make the release available to all devices.',
  },
  released: {
    nextStatus: 'deprecated',
    label: 'Deprecate',
    description: 'This will mark the release as deprecated. Devices will stop using it.',
  },
};

export default function FirmwareReleases() {
  const { releases, isLoading, error, promoteRelease, isPromoting } = useGatewayFirmwareReleases();
  const { devices } = useGatewayDevices();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [promoteTarget, setPromoteTarget] = useState<{
    release: GatewayFirmwareRelease;
    nextStatus: ReleaseStatus;
    label: string;
    description: string;
  } | null>(null);

  // Device version distribution
  const versionDist = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of devices) {
      const ver = d.firmware_version ?? 'Unknown';
      map.set(ver, (map.get(ver) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([version, count]) => ({ version, count }));
  }, [devices]);

  // Channel distribution
  const channelDist = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of devices) {
      map.set(d.ota_channel, (map.get(d.ota_channel) ?? 0) + 1);
    }
    return Array.from(map.entries()).map(([channel, count]) => ({ channel, count }));
  }, [devices]);

  const handlePromote = async () => {
    if (!promoteTarget) return;
    try {
      await promoteRelease({
        releaseId: promoteTarget.release.id,
        nextStatus: promoteTarget.nextStatus,
      });
      toast.success(`Release ${promoteTarget.release.version} promoted to ${promoteTarget.nextStatus}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to promote release');
    }
    setPromoteTarget(null);
  };

  return (
    <div className="space-y-5 px-6 py-5 max-w-[1200px] mx-auto">
      <h1 className="text-base font-semibold text-wt-text-primary">Firmware Releases</h1>

      {/* Release list */}
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-wt-text-primary flex items-center gap-2">
            <Package className="w-4 h-4" />
            Releases
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="py-8 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-wt-text-secondary" />
            </div>
          ) : error ? (
            <p className="py-6 text-center text-sm text-wt-red-primary">Failed to load releases</p>
          ) : releases.length === 0 ? (
            <p className="py-6 text-center text-sm text-wt-text-secondary">
              No firmware releases. Upload a binary to create the first release.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-wt-text-secondary text-left">
                    <th className="px-4 py-2 font-medium">Version</th>
                    <th className="px-4 py-2 font-medium">Hardware</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium text-right">Size</th>
                    <th className="px-4 py-2 font-medium">Min Prev</th>
                    <th className="px-4 py-2 font-medium">Created</th>
                    <th className="px-4 py-2 font-medium">Released</th>
                    <th className="px-4 py-2 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {releases.map((release) => {
                    const statusConfig = RELEASE_STATUS_CONFIG[release.release_status];
                    const promotion = PROMOTION_MAP[release.release_status];
                    const isExpanded = expandedId === release.id;

                    return (
                      <ReleaseRow
                        key={release.id}
                        release={release}
                        statusConfig={statusConfig}
                        promotion={promotion}
                        expanded={isExpanded}
                        isPromoting={isPromoting}
                        onToggle={() => setExpandedId(isExpanded ? null : release.id)}
                        onPromote={() => {
                          if (promotion) {
                            setPromoteTarget({
                              release,
                              nextStatus: promotion.nextStatus,
                              label: promotion.label,
                              description: promotion.description,
                            });
                          }
                        }}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Device firmware distribution */}
      {devices.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-wt-text-primary">
                Firmware Versions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {versionDist.map(({ version, count }) => (
                <div key={version} className="flex items-center justify-between text-sm">
                  <span className="font-mono-numeric text-wt-text-primary">{version}</span>
                  <span className="font-mono-numeric text-wt-text-secondary">
                    {count} device{count !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-wt-bg-secondary border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-wt-text-primary">
                OTA Channels
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {channelDist.map(({ channel, count }) => (
                <div key={channel} className="flex items-center justify-between text-sm">
                  <span className="text-wt-text-primary capitalize">{channel}</span>
                  <span className="font-mono-numeric text-wt-text-secondary">
                    {count} device{count !== 1 ? 's' : ''}
                  </span>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Promotion confirmation */}
      <AlertDialog open={!!promoteTarget} onOpenChange={() => setPromoteTarget(null)}>
        <AlertDialogContent className="bg-wt-bg-secondary border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-wt-text-primary">
              {promoteTarget?.label}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-wt-text-secondary">
              {promoteTarget?.description}
              {' '}Version: <span className="font-mono-numeric">{promoteTarget?.release.version}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePromote} disabled={isPromoting}>
              {isPromoting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReleaseRow
// ---------------------------------------------------------------------------

function ReleaseRow({
  release,
  statusConfig,
  promotion,
  expanded,
  isPromoting,
  onToggle,
  onPromote,
}: {
  release: GatewayFirmwareRelease;
  statusConfig: { label: string; color: string };
  promotion: { label: string } | undefined;
  expanded: boolean;
  isPromoting: boolean;
  onToggle: () => void;
  onPromote: () => void;
}) {
  return (
    <>
      <tr className="border-b border-border/50 hover:bg-wt-bg-tertiary/50">
        <td className="px-4 py-2 font-mono-numeric text-wt-text-primary">{release.version}</td>
        <td className="px-4 py-2 text-wt-text-secondary">{release.hardware_model}</td>
        <td className="px-4 py-2">
          <Badge variant="outline" className={`text-xs ${statusConfig.color}`}>
            {statusConfig.label}
          </Badge>
        </td>
        <td className="px-4 py-2 text-right font-mono-numeric text-wt-text-secondary">
          {(release.size_bytes / 1024).toFixed(0)} KB
        </td>
        <td className="px-4 py-2 font-mono-numeric text-wt-text-secondary">
          {release.min_previous_version ?? '--'}
        </td>
        <td className="px-4 py-2 text-wt-text-secondary">
          {format(new Date(release.created_at), 'yyyy-MM-dd')}
        </td>
        <td className="px-4 py-2 text-wt-text-secondary">
          {release.released_at ? format(new Date(release.released_at), 'yyyy-MM-dd') : '--'}
        </td>
        <td className="px-4 py-2 text-right">
          <div className="flex items-center justify-end gap-1">
            {promotion && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onPromote} disabled={isPromoting}>
                {promotion.label}
              </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7 px-1.5" onClick={onToggle}>
              {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-wt-bg-tertiary/30">
          <td colSpan={8} className="px-4 py-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs max-w-md">
              <span className="text-wt-text-secondary">SHA-256</span>
              <span className="font-mono-numeric break-all">{release.sha256}</span>
              {release.release_notes && (
                <>
                  <span className="text-wt-text-secondary">Notes</span>
                  <span className="text-wt-text-primary">{release.release_notes}</span>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
